<template>
  <div>
    <spinner v-if="loading"></spinner>
    <div v-else-if="error">
      <p class="m-y-none text-red-600 text-12">{{ errorMessage }}</p>
    </div>
    <div v-else class="flex flex-col gap-y-xs m-t-xs">
      <div class="bg-white rounded p-xs">
        <p class="m-b-none text-12 text-gray-600 uppercase">Next payout</p>
        <p class="m-y-none text-18 font-heavy" :class="{ 'text-red-600': owed.coach_pay < 0 }">{{ formatAsCurrency(owed.coach_pay) }}</p>
        <p class="m-y-none text-12 text-gray-600">
          {{ owed.lesson_count }} completed {{ owed.lesson_count === 1 ? 'lesson' : 'lessons' }} since {{ formatDate(summary.owed_since) }}
        </p>
        <p v-if="owed.uncollected_count > 0" class="m-y-none text-12 text-yellow-700">
          {{ owed.uncollected_count }} {{ owed.uncollected_count === 1 ? 'lesson' : 'lessons' }} awaiting parent payment
        </p>
        <p v-if="owed.coach_pay < 0" class="m-y-none text-12 text-red-600">
          Refunds exceeded new lessons &mdash; this offsets your next payout.
        </p>
      </div>

      <div class="bg-white rounded p-xs">
        <p v-if="summary.last_payout && summary.last_payout.amount_cents > 0" class="m-y-none text-12 text-gray-600">
          Last paid {{ formatDate(summary.last_payout.paid_on) }} · {{ formatAsCurrency(summary.last_payout.amount_cents) }}
        </p>
        <p v-else class="m-y-none text-12 text-gray-600">
          No payouts recorded yet
        </p>
        <p v-if="summary.paid_this_year_cents > 0" class="m-y-none text-12 text-gray-600">
          Paid this year: {{ formatAsCurrency(summary.paid_this_year_cents) }}
        </p>

        <p class="m-t-xs m-b-none">
          <a class="text-12 cursor-pointer text-gray-600" @click="toggleHistory">
            <i :class="showHistory ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"></i>
            Payout history
          </a>
        </p>
        <div v-if="showHistory" class="m-t-xxs">
          <p v-if="historyLoading" class="m-y-none text-12 text-gray-500"><i class="fa-solid fa-spinner fa-spin"></i></p>
          <p v-else-if="historyError" class="m-y-none text-12 text-red-600">{{ historyError }}</p>
          <p v-else-if="realPayouts.length === 0" class="m-y-none text-12 text-gray-500">No payouts recorded yet.</p>
          <p v-for="payout in realPayouts" :key="payout.id" class="m-y-none text-12 text-gray-600 pl-slot-time">
            {{ formatDate(payout.paid_on) }} · {{ formatAsCurrency(payout.amount_cents) }}
            <span class="text-gray-500">through {{ formatDate(payout.period_end_at) }}</span>
          </p>
        </div>
      </div>

      <p class="m-y-none text-12 text-gray-500">
        Pay is lesson revenue collected minus the facility fee, counted once the parent pays.
      </p>
    </div>
  </div>
</template>

<script>
import { currencyMix } from '../../shared/currency_mix';
import { errorableMix } from './mixins/errorable_mix';
import Spinner from './spinner.vue';

// Read-only mirror of this coach's row on the admin coach pay page: owed since
// their last payout (same Poro window server-side, so both sides see the same
// number) plus their payout ledger. Deliberately not a calendar-month tally —
// months never line up with payout periods, and two different numbers for the
// same money is how "why am I short?" conversations start.
export default {
  name: 'coach-lesson-earnings',
  components: { Spinner },
  mixins: [currencyMix, errorableMix],
  props: {
    coachId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      summary: null,
      showHistory: false,
      historyLoading: false,
      historyError: null,
      history: [],
    };
  },
  computed: {
    owed() {
      return this.summary.owed;
    },
    // $0 rows are starting-point markers, not money the coach received
    realPayouts() {
      return this.history.filter((payout) => payout.amount_cents > 0);
    },
  },
  created() {
    this.loadEarnings();
  },
  methods: {
    loadEarnings() {
      const params = new URLSearchParams({ coach_id: this.coachId });
      axios.get(`/api/v1/private_lessons/earnings_summary?${params.toString()}`)
        .then((response) => {
          this.summary = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.errorMessage = 'Unable to load earnings right now.';
          this.error = true;
          this.loading = false;
        });
    },
    toggleHistory() {
      if (this.showHistory) {
        this.showHistory = false;
        return;
      }
      this.showHistory = true;
      this.historyLoading = true;
      this.historyError = null;
      this.$apiService.loadAllPages(this.$apiService.coachPayoutsUrl(this.coachId), 'coach_payouts')
        .then((payouts) => {
          this.history = payouts;
          this.historyLoading = false;
        })
        .catch(() => {
          // Local to the history panel — the top-level error would hide the
          // already-loaded earnings summary
          this.historyLoading = false;
          this.historyError = 'Unable to load payout history right now.';
        });
    },
    formatDate(date) {
      return moment(date).format('MMM D, YYYY');
    },
  },
};
</script>
